import { useEffect, useState } from "react";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { X, Printer, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SUFFLE_LOGO = "/suffle-logo-new.png?v=2";
const FMT = {
  "4x6": { w: "4in", h: "6in", page: "4in 6in", label: "4×6 Thermal" },
  a5: { w: "148mm", h: "210mm", page: "A5", label: "A5" },
  a4: { w: "210mm", h: "297mm", page: "A4", label: "A4" },
};

const line = (a, keys) => keys.map((k) => a?.[k]).filter(Boolean).join(", ");

/* The actual print-ready label. Reflows by format; never stretches one design onto another. */
function Label({ data, s, fmt, company }) {
  const compact = fmt === "4x6";
  const d = data;
  const seller = d.seller || {};
  const cust = d.customer || {};
  const order = d.order || {};
  const ship = d.shipment || {};
  const items = order.items || [];
  const awb = ship.awb || "";
  const barH = compact ? 46 : 60;
  const barW = compact ? 1.6 : 2;

  const SellerBrand = (
    <div className="flex items-center gap-2 min-w-0">
      {s.seller_logo && seller.logo
        ? <img src={seller.logo} alt="" style={{ maxHeight: s.seller_logo_size, maxWidth: compact ? 120 : 180 }} className="h-auto w-auto object-contain" />
        : null}
      <div className="min-w-0">
        <p className={`font-extrabold leading-tight text-black ${compact ? "text-[13px]" : "text-base"}`} style={{ wordBreak: "break-word" }}>{seller.name}</p>
        <p className="text-[9px] font-semibold uppercase tracking-wide text-black/70">Seller / Sender</p>
      </div>
    </div>
  );

  const ShipTo = (
    <div className="border-y-2 border-black py-1.5">
      <p className="text-[9px] font-bold uppercase tracking-widest text-black/70">Ship To</p>
      <p className={`font-extrabold text-black ${compact ? "text-[15px]" : "text-lg"} leading-tight`}>{cust.name || "—"}</p>
      <p className={`text-black ${compact ? "text-[12px]" : "text-sm"} leading-snug`} style={{ wordBreak: "break-word" }}>
        {line(cust, ["line1", "line2", "district", "city", "state"])}
      </p>
      <p className={`font-bold text-black ${compact ? "text-[14px]" : "text-base"}`}>PIN: {cust.pincode || "—"}</p>
      {s.show_customer_phone && cust.phone && <p className={`text-black ${compact ? "text-[12px]" : "text-sm"}`}>Ph: {cust.phone}</p>}
    </div>
  );

  const OrderInfo = s.show_order_info && (
    <div className={`grid grid-cols-2 gap-x-2 ${compact ? "text-[11px]" : "text-sm"}`}>
      <p className="text-black"><span className="text-black/60">Order</span> <b>{order.number || "—"}</b></p>
      <p className="text-black"><span className="text-black/60">Date</span> <b>{order.date || "—"}</b></p>
      {order.master_number && order.master_number !== order.number && <p className="text-black"><span className="text-black/60">Master</span> <b>{order.master_number}</b></p>}
      {s.show_courier && ship.courier && <p className="text-black"><span className="text-black/60">Courier</span> <b>{ship.courier}</b></p>}
    </div>
  );

  const Products = s.show_product_info && items.length > 0 && (
    <div className={`border-t border-black/30 pt-1 ${compact ? "text-[11px]" : "text-sm"}`}>
      {items.map((it, i) => (
        <div key={i} className="mb-0.5">
          <p className="font-semibold text-black leading-tight" style={{ wordBreak: "break-word" }}>{it.name}</p>
          <p className="text-black/70">
            {s.show_variant && it.variant ? `${it.variant} · ` : ""}
            {s.show_sku && it.sku ? `SKU: ${it.sku} · ` : ""}
            {s.show_qty ? `Qty: ${it.qty}` : ""}
          </p>
        </div>
      ))}
    </div>
  );

  const AwbBlock = s.show_awb && (
    awb ? (
      <div className="flex flex-col items-center border-y-2 border-black py-1.5">
        {s.show_barcode
          ? <Barcode value={awb} format="CODE128" height={barH} width={barW} fontSize={compact ? 12 : 14} margin={0} background="#ffffff" lineColor="#000000" />
          : <p className="font-mono text-lg font-bold text-black">{awb}</p>}
        <p className={`mt-0.5 font-bold text-black ${compact ? "text-[12px]" : "text-sm"}`}>AWB: {awb}</p>
      </div>
    ) : (
      <div className="border-y-2 border-black py-2 text-center">
        <p className="text-[11px] font-semibold text-black/60">AWB pending — assign a tracking number before dispatch</p>
      </div>
    )
  );

  const ReturnTo = s.show_return_address && (seller.return_address?.line1) && (
    <p className={`text-black/70 ${compact ? "text-[9px]" : "text-[11px]"} leading-tight`}>
      <b>RETURN TO:</b> {seller.return_address.name}, {line(seller.return_address, ["line1", "city", "state", "pincode"])}
      {s.show_customer_phone && seller.return_address.phone ? ` · ${seller.return_address.phone}` : ""}
    </p>
  );

  const SuffleFooter = (
    <div className="mt-auto flex items-center justify-between border-t-2 border-black pt-1.5">
      <div className="min-w-0">
        <p className={`font-bold text-black ${compact ? "text-[11px]" : "text-sm"}`}>{company?.brand_name || "Suffle Online"}</p>
        <p className={`text-black ${compact ? "text-[11px]" : "text-sm"}`}>{company?.website || "www.suffleonline.com"}</p>
        {!compact && s.footer_text && <p className="text-[10px] text-black/60">{s.footer_text}</p>}
      </div>
      <div className="flex items-center gap-2">
        {s.show_qr && awb && <QRCodeSVG value={`https://www.suffleonline.com/track/${awb}`} size={compact ? 42 : 60} bgColor="#ffffff" fgColor="#000000" level="M" />}
        <img src={SUFFLE_LOGO} alt="Suffle Online" style={{ height: s.suffle_logo_size, maxWidth: compact ? 60 : 90 }} className="w-auto object-contain" />
      </div>
    </div>
  );

  return (
    <div
      id="ship-label-print"
      className="flex flex-col bg-white text-black"
      style={{ width: FMT[fmt].w, height: fmt === "a4" ? "auto" : FMT[fmt].h, minHeight: FMT[fmt].h, padding: compact ? "10px" : "16px", boxSizing: "border-box", fontFamily: "Arial, Helvetica, sans-serif" }}
      data-testid="shipping-label"
    >
      <div className="flex items-center justify-between gap-2 pb-1.5">
        {SellerBrand}
        <span className={`shrink-0 rounded border border-black px-1.5 py-0.5 font-bold text-black ${compact ? "text-[10px]" : "text-xs"}`}>{ship.method || "Prepaid"}</span>
      </div>
      <div className={`flex flex-col ${compact ? "gap-1.5" : "gap-2.5"} flex-1`}>
        {ShipTo}
        {OrderInfo}
        {AwbBlock}
        {Products}
        {ReturnTo}
      </div>
      {SuffleFooter}
    </div>
  );
}

export function ShippingLabelModal({ open, onClose, suborderId }) {
  const [data, setData] = useState(null);
  const [s, setS] = useState(null);
  const [company, setCompany] = useState(null);
  const [fmt, setFmt] = useState("4x6");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/company").then((r) => setCompany(r.data.company)).catch(() => {}); }, []);

  useEffect(() => {
    if (!open || !suborderId) return;
    setLoading(true);
    Promise.all([
      api.get(`/marketplace/seller/orders/${suborderId}/label-data`),
      api.get(`/marketplace/label-settings`),
    ]).then(([ld, ls]) => {
      setData(ld.data);
      setS(ls.data.settings);
      setFmt(ls.data.settings?.default_format || "4x6");
    }).catch((e) => { toast.error(formatApiError(e?.response?.data?.detail) || "Could not load label"); onClose?.(); })
      .finally(() => setLoading(false));
  }, [open, suborderId]); // eslint-disable-line

  useEffect(() => {
    let el = document.getElementById("ship-page-style");
    if (!el) { el = document.createElement("style"); el.id = "ship-page-style"; document.head.appendChild(el); }
    el.textContent = `@media print { @page { size: ${FMT[fmt].page}; margin: 0; } body * { visibility: hidden !important; } #ship-label-print, #ship-label-print * { visibility: visible !important; } #ship-label-print { position: fixed !important; top: 0; left: 0; box-shadow: none !important; } }`;
    return () => {};
  }, [fmt, open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-auto bg-black/60 p-4" data-testid="shipping-label-modal" onClick={onClose}>
      <div className="my-6 w-full max-w-3xl rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 no-print">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Shipping label</h3>
            <p className="text-xs text-slate-500">Seller branding + Suffle brand identity · print-ready</p>
          </div>
          <div className="flex items-center gap-2">
            {Object.entries(FMT).map(([k, v]) => (
              <button key={k} onClick={() => setFmt(k)} data-testid={`label-fmt-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${fmt === k ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600 hover:border-blue-300"}`}>{v.label}</button>
            ))}
            <button onClick={() => window.print()} disabled={loading || !data} data-testid="label-print-btn" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"><Printer className="h-4 w-4" /> Print</button>
            <button onClick={onClose} data-testid="label-close-btn" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex justify-center rounded-xl bg-slate-100 p-4">
          {loading || !data || !s
            ? <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            : <div className="shadow-lg ring-1 ring-slate-300"><Label data={data} s={s} fmt={fmt} company={company} /></div>}
        </div>
      </div>
    </div>
  );
}
